import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, MessageSquare, Mail, HeartHandshake, Download, LogOut, Home, BadgeCheck } from "lucide-react";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`admin-stat-${label}`}>
    <div className="flex items-center justify-between">
      <span className="font-mono-accent text-xs uppercase tracking-widest text-slate-500">{label}</span>
      <Icon size={18} style={{ color: accent }} />
    </div>
    <div className="mt-3 font-display font-black text-3xl text-[#0B1121] tabular-nums">{value}</div>
  </div>
);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const [summary, setSummary] = useState({});
  const [supporters, setSupporters] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [adhesions, setAdhesions] = useState([]);
  const [membersOnly, setMembersOnly] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, sup, msg, news, don] = await Promise.all([
          api.get("/admin/summary"),
          api.get("/admin/supporters"),
          api.get("/admin/messages"),
          api.get("/admin/newsletter"),
          api.get("/admin/adhesions"),
        ]);
        setSummary(s.data);
        setSupporters(sup.data);
        setMessages(msg.data);
        setNewsletter(news.data);
        setAdhesions(don.data);
      } catch {
        toast.error("Erreur de chargement des données.");
      }
    };
    load();
  }, []);

  const exportCsv = async (path, filename) => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export téléchargé.");
    } catch { toast.error("Export impossible."); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-[#0B1121] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center h-9 w-9 rounded-full bg-white shrink-0">
              <img src="/prr-logo.png" alt="Logo PRR" className="h-6 w-6 object-contain" />
            </span>
            <span className="font-display font-extrabold tracking-tight">PRR · Administration</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-white/60">{admin?.email}</span>
            <a href="/" className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors" data-testid="admin-home-link">
              <Home size={16} /> Site
            </a>
            <button onClick={logout} data-testid="admin-logout"
              className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <Stat label="Soutiens" value={summary.supporters ?? 0} icon={Users} accent="#001489" />
          <Stat label="Adhérents" value={summary.members ?? 0} icon={BadgeCheck} accent="#16a34a" />
          <Stat label="Bénévoles" value={summary.volunteers ?? 0} icon={Users} accent="#38BDF8" />
          <Stat label="Messages" value={summary.messages ?? 0} icon={MessageSquare} accent="#E1000F" />
          <Stat label="Newsletter" value={summary.newsletter ?? 0} icon={Mail} accent="#001489" />
          <Stat label="Adhésions (€)" value={summary.adhesions_total ?? 0} icon={HeartHandshake} accent="#E1000F" />
        </div>

        <Tabs defaultValue="supporters">
          <TabsList data-testid="admin-tabs" className="mb-6">
            <TabsTrigger value="supporters" data-testid="tab-supporters">Soutiens</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="newsletter" data-testid="tab-newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="donations" data-testid="tab-adhesions">Adhésions</TabsTrigger>
          </TabsList>

          <TabsContent value="supporters">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <button
                onClick={() => setMembersOnly((v) => !v)}
                data-testid="filter-members-only"
                className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg border transition-colors ${
                  membersOnly
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-[#0B1121] border-slate-300 hover:border-[#0B1121]"
                }`}
              >
                <BadgeCheck size={16} /> {membersOnly ? "Adhérents uniquement" : "Tous les soutiens"}
              </button>
              <button onClick={() => exportCsv("/admin/export/supporters.csv", "soutiens.csv")}
                data-testid="export-supporters" className="flex items-center gap-2 text-sm bg-[#0B1121] text-white px-4 py-2 rounded-lg hover:bg-[#001489] transition-colors">
                <Download size={16} /> Exporter CSV
              </button>
            </div>
            <TableCard headers={["Prénom", "Nom", "Email", "Ville", "CP", "Bénévole", "Adhérent", "Date"]}
              rows={supporters.filter((s) => !membersOnly || s.member).map((s) => [s.first_name, s.last_name, s.email, s.city, s.postal_code, s.volunteer ? "Oui" : "Non", s.member ? "✓ Adhérent" : "—", fmtDate(s.created_at)])}
              empty={membersOnly ? "Aucun adhérent pour l'instant." : "Aucun soutien pour l'instant."} testid="table-supporters" />
          </TabsContent>

          <TabsContent value="messages">
            <div className="flex justify-end mb-3">
              <button onClick={() => exportCsv("/admin/export/messages.csv", "messages.csv")}
                data-testid="export-messages" className="flex items-center gap-2 text-sm bg-[#0B1121] text-white px-4 py-2 rounded-lg hover:bg-[#001489] transition-colors">
                <Download size={16} /> Exporter CSV
              </button>
            </div>
            <TableCard headers={["Nom", "Email", "Sujet", "Message", "Date"]}
              rows={messages.map((m) => [m.name, m.email, m.subject, m.message, fmtDate(m.created_at)])}
              empty="Aucun message reçu." testid="table-messages" />
          </TabsContent>

          <TabsContent value="newsletter">
            <TableCard headers={["Email", "Date"]}
              rows={newsletter.map((n) => [n.email, fmtDate(n.created_at)])}
              empty="Aucun inscrit à la newsletter." testid="table-newsletter" />
          </TabsContent>

          <TabsContent value="donations">
            <TableCard headers={["Adhérent", "Email", "Montant (€)", "Statut", "Date"]}
              rows={adhesions.map((d) => [d.donor_name || "—", d.donor_email || "—", d.amount, d.payment_status, fmtDate(d.created_at)])}
              empty="Aucune adhésion enregistrée." testid="table-adhesions" />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const TableCard = ({ headers, rows, empty, testid }) => (
  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={testid}>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {headers.map((h) => (
              <th key={h} className="text-left font-mono-accent text-xs uppercase tracking-wider text-slate-500 px-4 py-3 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="px-4 py-10 text-center text-slate-400">{empty}</td></tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                {r.map((c, j) => (
                  <td key={j} className="px-4 py-3 text-slate-700 max-w-xs truncate">{String(c)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default AdminDashboard;
